import { useState } from 'react';
import {
  STAGE_LABEL,
  formatSpan,
  secondsSinceReceived,
  workSummary,
  type StageHistoryEntry,
  type WorkStep,
} from '@/domain';
import { useNow } from '@/application/timer/timerStore';
import { formatDay, formatInstant, formatTime } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import styles from './WorkTimeline.module.css';

const text = strings.finalCheck.work;

export interface WorkTimelineProps {
  jobOrderNo: string;
  /** When the case was booked in. Absent from an API older than the field. */
  receivedAt: string | null | undefined;
  /** The bench entries of the tray, in any order. */
  history: readonly StageHistoryEntry[];
}

/**
 * The work on a case, for whoever passes or fails it. The request of
 * 7 October 2026, section 4.
 *
 * When the lab received the case and how long it has had it, then who worked
 * it, in order: one line a step with the department, the technician, the start
 * and the finish and the time worked, and a mark on every flag and every
 * rework. A line opens to its details, and one button opens them all.
 *
 * The history card further down the record has the same bench entries among
 * every other action, newest first. This is the reading a decision needs:
 * oldest first, nothing but the work.
 */
export function WorkTimeline({ jobOrderNo, receivedAt, history }: WorkTimelineProps) {
  const nowMs = useNow();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const summary = workSummary(history, nowMs);
  const inLab = secondsSinceReceived(receivedAt, nowMs);
  const allOpen = summary.steps.length > 0 && summary.steps.every((step) => open.has(step.id));
  const headingId = `work-${jobOrderNo}`;

  function toggle(id: string) {
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <div className={styles.head}>
        <h4 id={headingId}>{text.title}</h4>
        {summary.steps.length > 0 && (
          <button
            type="button"
            className={styles.all}
            aria-pressed={allOpen}
            onClick={() =>
              setOpen(allOpen ? new Set() : new Set(summary.steps.map((step) => step.id)))
            }
          >
            {allOpen ? text.hideAll : text.showAll}
          </button>
        )}
      </div>

      <dl className={styles.facts}>
        {receivedAt != null && (
          <div>
            <dt>{text.received}</dt>
            <dd>
              <time dateTime={receivedAt}>{formatInstant(receivedAt)}</time>
            </dd>
          </div>
        )}
        {inLab !== null && (
          <div>
            <dt>{text.inLab}</dt>
            <dd>{formatSpan(inLab)}</dd>
          </div>
        )}
        <div>
          <dt>{text.worked}</dt>
          <dd>{formatSpan(summary.workedSeconds)}</dd>
        </div>
        {summary.flags + summary.reworks > 0 && (
          <div>
            <dt>{text.sentBack}</dt>
            <dd className={styles.counts}>
              {summary.flags > 0 && <span className={styles.flag}>{text.flags(summary.flags)}</span>}
              {summary.reworks > 0 && (
                <span className={styles.rework}>{text.reworks(summary.reworks)}</span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {summary.steps.length === 0 ? (
        <p className={styles.none}>{text.none}</p>
      ) : (
        <ol className={styles.steps} aria-label={text.stepsLabel}>
          {summary.steps.map((step) => (
            <Step
              key={step.id}
              step={step}
              open={open.has(step.id)}
              onToggle={() => toggle(step.id)}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

/** `2026-10-05 09:12 to 10:40`, with the day said again only when the finish fell on another. */
function when(step: WorkStep): string {
  if (step.endedAt === null) return `${formatInstant(step.startedAt)}, ${text.open}`;
  const finish =
    formatDay(step.endedAt) === formatDay(step.startedAt)
      ? formatTime(step.endedAt)
      : formatInstant(step.endedAt);
  return `${formatInstant(step.startedAt)} ${text.to} ${finish}`;
}

function Step({
  step,
  open,
  onToggle,
}: {
  step: WorkStep;
  open: boolean;
  onToggle: () => void;
}) {
  const detailsId = `work-step-${step.id}`;
  const marked = step.sent !== null || step.after !== null;

  return (
    <li className={`${styles.step} ${marked ? styles.marked : ''}`}>
      <button
        type="button"
        className={styles.line}
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={onToggle}
      >
        <span className={styles.stage}>{STAGE_LABEL[step.stage]}</span>
        <span className={styles.who}>{step.byUserName ?? text.nobody}</span>
        <span className={styles.when}>{when(step)}</span>
        <span className={styles.spent}>
          {step.activeSeconds > 0 ? formatSpan(step.activeSeconds) : text.noTime}
        </span>
        {step.after === 'rework' && <span className={styles.rework}>{text.rework}</span>}
        {step.after === 'flag' && <span className={styles.flag}>{text.afterFlag}</span>}
        {step.sent === 'rework' && <span className={styles.rework}>{text.failed}</span>}
        {step.sent === 'flag' && <span className={styles.flag}>{text.flagged}</span>}
      </button>

      {open && (
        <dl id={detailsId} className={styles.details}>
          <Detail label={text.department} value={STAGE_LABEL[step.stage]} />
          <Detail label={text.technician} value={step.byUserName ?? text.nobody} />
          <Detail label={text.started} value={formatInstant(step.startedAt)} />
          <Detail
            label={text.finished}
            value={step.endedAt === null ? text.notFinished : formatInstant(step.endedAt)}
          />
          <Detail
            label={text.timeWorked}
            value={step.activeSeconds > 0 ? formatSpan(step.activeSeconds) : text.noTime}
          />
          <Detail label={text.startToFinish} value={formatSpan(step.elapsedSeconds)} />
          {step.after !== null && (
            <Detail
              label={text.why}
              value={step.after === 'rework' ? text.whyRework : text.whyFlag}
            />
          )}
          {step.sent !== null && (
            <Detail
              label={step.sent === 'rework' ? text.reasonRework : text.reasonFlag}
              value={step.reason ?? ''}
            />
          )}
          {step.systemNote !== null && <Detail label={text.note} value={step.systemNote} />}
        </dl>
      )}
    </li>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
